import {
  capitalize,
  every,
  find,
  get,
  isEmpty,
  keys,
  map,
  orderBy,
  toLower,
  values,
  words
} from "lodash";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import ExternalLink from "../../../components/ExternalLink";
import FeatureExplanation from "../../../components/FeatureExplanation";
import InfoIcon from "../../../components/icons/InfoIcon";
import FilterLabels from "../../../components/labels/FilterLabels";
import Labels from "../../../components/labels/Labels";
import TruncatedText from "../../../components/TruncatedText";
import { useAuthentication } from "../../../context/authentication";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useHotspots } from "../../../context/hotspots";
import { useLabels } from "../../../context/labels";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { useMembers } from "../../../context/members";
import { useToast } from "../../../context/toast";
import { useWallets } from "../../../context/wallets";
import { useWorkspaces } from "../../../context/workspaces";
import { Entity } from "../../../utils/constants";
import { formatDateToLocale } from "../../../utils/dates";
import { Wallet } from "../../../utils/types";
import useDebounce from "../../../utils/useDebounce";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import { useHntMarket } from "../../../utils/useHntMarket";
import EntityDataTable from "../common/EntityDataTable";
import MemberCell from "../common/MemberCell";
import PaymentTransactionsDataTable from "../common/PaymentTransactionsDataTable";
import TablePropertiesSidebar from "../common/TablePropertiesSidebar";
import TableSearchInput from "../common/TableSearchInput";
import useDataTable, { Property } from "../common/useDataTable";
import { PaginationKey } from "../common/usePagination";
import { formatMobileBalance } from "../Dashboard/WalletsSection";
import LockedWorkspace from "../LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../LockedWorkspaceByMembers";
import WalletVerificationCountdown from "../VerifyWallet/WalletVerificationCountdown";
import WalletActionsButton from "./WalletActionsButton";

interface Props {}

const WalletsData: React.FC<Props> = () => {
  const { setModalWarningConfig } = useToast();
  const { wrapActionWithAuth } = useAuthentication();
  const { wallets, toggleAddWalletSidebar, removeAllWallets } = useWallets();
  const { workspaceHotspots, hotspotRewardsSummary } = useHotspots();
  const { currentWorkspaceIsLocked, currentWorkspaceAllowedForCommunityPlan } =
    useWorkspaces();
  const { labels, linkedLabels } = useLabels();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const sortOrder = useDataTable((state) => state.sortOrder[Entity.wallet]);
  const searchInput = useDataTable((state) => state.searchInput[Entity.wallet]);
  const debouncedSearchInput = useDebounce(searchInput, 200);
  const { displayInCurrency } = useHntMarket();
  const smartLabelFilters = useMemo(() => ({}), []);
  const { getIsReady } = useLoading();
  const { membersLimitExceeded } = useMembers();

  const lifetimeRewardsByWallet = useMemo(
    () =>
      workspaceHotspots.reduce(
        (acc: { [walletId: string]: number }, hotspot) => {
          acc[hotspot.owner] =
            (acc[hotspot.owner] || 0) +
            get(hotspotRewardsSummary, [hotspot.id, "lifetime"], 0);
          return acc;
        },
        {}
      ),
    [hotspotRewardsSummary, workspaceHotspots]
  );

  const filteredWalletLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.workspaceWallets],
        labels,
        smartLabelFilters,
        Entity.wallet
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredWallets = useMemo(() => {
    let filteredList = values(wallets).filter((wallet) => {
      if (
        !isEmpty(filteredWalletLabels) &&
        !every(filteredWalletLabels, ({ uuid }) => {
          const smartFilterFunction = (smartLabelFilters as any)[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(wallet)
            : (linkedLabels[wallet.uuid] || []).includes(uuid);
        })
      ) {
        return false;
      }

      if (debouncedSearchInput) {
        const searchInputWords = words(debouncedSearchInput).map(toLower);

        return every(searchInputWords, (word) =>
          toLower(wallet.address).includes(word)
        );
      }
      return true;
    });

    if (sortOrder) {
      type Iteratee = string | ((wallet: Wallet) => number);
      let iteratee: Iteratee = sortOrder.iteratee;

      if (sortOrder.iteratee === "lifetimeRewardsByWallet") {
        iteratee = (wallet) => get(lifetimeRewardsByWallet, wallet.address, 0);
      }

      filteredList = orderBy(filteredList, [iteratee], [sortOrder.order]);
    }

    return filteredList;
  }, [
    wallets,
    sortOrder,
    filteredWalletLabels,
    debouncedSearchInput,
    smartLabelFilters,
    linkedLabels,
    lifetimeRewardsByWallet
  ]);

  const properties: { [key: string]: Property<Wallet> } = useMemo(
    () => ({
      address: {
        key: "address",
        title: "Wallets",
        style: { minWidth: "14rem" },
        sortIteratee: "address",
        renderContent: (wallet) => (
          <>
            <TruncatedText text={wallet.address} label="wallet address" />
            <ExternalLink
              className="mt-1 text-xs text-gray-500"
              text="View in Helium explorer"
              url={`https://explorer.helium.com/accounts/${wallet.address}`}
            />
          </>
        )
      },
      labels: {
        key: "labels",
        title: "Labels",
        style: { minWidth: "20rem" },
        renderContent: (wallet) => (
          <Labels
            entity={Entity.wallet}
            linkedUuid={wallet.uuid}
            filterKey={LabelFilterKey.workspaceWallets}
          />
        )
      },
      "balance_data.balance": {
        key: "balance_data.balance",
        title: "HNT",
        style: { minWidth: "9rem" },
        sortIteratee: "balance_data.balance",
        renderContent: (wallet) =>
          displayInCurrency(wallet.balance_data.balance, {
            needsDivision: true
          })
      },
      "balance_data.mobile_balance": {
        key: "balance_data.mobile_balance",
        title: "MOBILE",
        style: { minWidth: "9rem" },
        sortIteratee: "balance_data.mobile_balance",
        renderContent: (wallet) =>
          formatMobileBalance(wallet.balance_data.mobile_balance)
      },
      "balance_data.iot_balance": {
        key: "balance_data.iot_balance",
        title: "IOT",
        style: { minWidth: "9rem" },
        sortIteratee: "balance_data.iot_balance",
        renderContent: (wallet) =>
          displayInCurrency(wallet.balance_data.iot_balance, {
            needsDivision: true
          })
      },
      "balance_data.dc_balance": {
        key: "balance_data.dc_balance",
        title: "DC",
        style: { minWidth: "9rem" },
        sortIteratee: "balance_data.dc_balance",
        renderContent: (wallet) =>
          displayInCurrency(wallet.balance_data.dc_balance, {
            needsDivision: true
          })
      },
      "balance_data.staked_balance": {
        key: "balance_data.staked_balance",
        title: "Staked HNT",
        style: { minWidth: "9rem" },
        sortIteratee: "balance_data.staked_balance",
        renderContent: (wallet) =>
          displayInCurrency(wallet.balance_data.staked_balance, {
            needsDivision: true
          })
      },
      hotspot_count: {
        key: "hotspot_count",
        title: "Hotspots",
        sortIteratee: "hotspot_count",
        renderContent: (wallet) => wallet.hotspot_count
      },
      lifetime: {
        key: "lifetime",
        title: "Lifetime",
        sortIteratee: "lifetimeRewardsByWallet",
        renderContent: (wallet) =>
          displayInCurrency(get(lifetimeRewardsByWallet, wallet.address, 0), {
            needsDivision: true
          })
      },
      tracked: {
        key: "tracked",
        title: "Tracked",
        sortIteratee: "tracked",
        renderContent: (wallet) => (wallet.tracked ? "Yes" : "No")
      },
      verified: {
        key: "verified",
        title: "Verified",
        sortIteratee: "verified",
        renderContent: (wallet) => (wallet.verified ? "Yes" : "No")
      },
      current_verification_status: {
        key: "current_verification_status",
        title: "Verification status",
        style: { minWidth: "12rem" },
        sortIteratee: "current_verification_status",
        renderContent: (wallet) => {
          if (
            !!find(wallet.verifications, {
              status: "completed"
            })
          ) {
            return "Completed by you";
          }

          const verification = find(wallet.verifications, {
            status: "pending"
          });

          if (
            !verification ||
            moment(verification.expires_at).utc().isBefore(moment().utc())
          ) {
            return wallet.current_verification_status === "completed"
              ? "Completed by others"
              : capitalize(wallet.current_verification_status);
          }

          return (
            <div className="flex items-center">
              {wallet.current_verification_status === "pending" ? (
                <WalletVerificationCountdown
                  walletAddress={wallet.address}
                  className="mr-2"
                />
              ) : (
                <span>{capitalize(wallet.current_verification_status)}</span>
              )}

              <Tooltip
                overlay={`Awaiting a payment of ${
                  verification.verification_amount
                }HNT from this wallet. This verification expires ${moment(
                  verification.expires_at
                )
                  .utc()
                  .fromNow()}`}
              >
                <InfoIcon className="ml-1 inline-flex h-4 w-4" />
              </Tooltip>
            </div>
          );
        }
      },
      created_at: {
        key: "created_at",
        title: "Added",
        style: { minWidth: "13rem" },
        sortIteratee: "created_at",
        renderContent: (wallet) => (
          <MemberCell
            memberUuid={wallet.created_by}
            subtitle={formatDateToLocale(wallet.created_at)}
          />
        )
      },
      updated_at: {
        key: "updated_at",
        title: "Updated",
        style: { minWidth: "13rem" },
        sortIteratee: "updated_at",
        renderContent: (wallet) =>
          wallet.updated_by ? (
            <MemberCell
              memberUuid={wallet.updated_by}
              subtitle={formatDateToLocale(wallet.updated_at)}
            />
          ) : (
            "-"
          )
      },
      actions: {
        key: "actions",
        title: "Actions",
        renderContent: (wallet) => <WalletActionsButton wallet={wallet} />
      }
    }),
    [displayInCurrency, lifetimeRewardsByWallet]
  );

  const isLoading = useMemo(
    () => isEmpty(wallets) && !getIsReady([LoadingKey.workspaceWallets]),
    [getIsReady, wallets]
  );

  const renderPageContent = useMemo(() => {
    if (currentWorkspaceIsLocked) {
      return (
        <LockedWorkspace
          title="Payment failed"
          paragraphs={[
            "Your subscription payment failed. Please resolve the issue with your payment method."
          ]}
          buttonText="Update payment details"
          buttonLink="/workspaces/settings/billing"
        />
      );
    }

    if (!currentWorkspaceAllowedForCommunityPlan) {
      return <LockedWorkspaceByCommunityPlan />;
    }

    if (membersLimitExceeded) {
      return <LockedWorkspaceByMembers />;
    }

    if (isLoading) return <CenteredLoading className="h-96" />;

    if (isEmpty(wallets)) {
      return (
        <FeatureExplanation
          title="Wallets"
          paragraphs={[
            <>
              Add wallets to your workspace to track their balances and{" "}
              <Link
                className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                to="/workspace/hotspots"
              >
                hotspots
              </Link>
              .
            </>,
            <>
              <Link
                className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                to="/workspace/wallets/verify/select-wallet-by-hotspot"
              >
                Wallet verification
              </Link>{" "}
              enables{" "}
              <ExternalLink
                className="inline-flex"
                url="https://hotspotty.net/collaborate-community-team/"
                text="community collaboration"
              />
              .
            </>
          ]}
        >
          <button
            id="add-wallet-button"
            type="button"
            className="mt-6 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={wrapActionWithAuth(() => toggleAddWalletSidebar())}
          >
            Add wallet
          </button>
        </FeatureExplanation>
      );
    }

    return (
      <>
        <div className="p-4">
          <TableSearchInput
            entity={Entity.wallet}
            placeholder="Search a wallet by address"
          />

          <div className="mt-2 flex items-center">
            <FilterLabels
              className="flex-1"
              entity={Entity.wallet}
              filteredLabels={filteredWalletLabels}
              smartLabels={keys(smartLabelFilters).map((key) =>
                getSmartLabelFromKey(key, Entity.wallet)
              )}
              filterKey={LabelFilterKey.workspaceWallets}
            />

            <DottedMenu
              text="Open options"
              menuItems={[
                {
                  text: "Edit columns",
                  onClick: () => {
                    setShowPropertiesSidebar(true);
                  }
                },
                {
                  onClick: wrapActionWithAuth(() => {
                    setModalWarningConfig({
                      open: true,
                      resolve: () => {
                        removeAllWallets();
                      }
                    });
                  }),
                  text: "Remove all wallets",
                  disabled:
                    isEmpty(wallets) ||
                    values(wallets).length !== filteredWallets.length
                }
              ]}
            />

            <button
              id="add-wallet-button"
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={wrapActionWithAuth(() => toggleAddWalletSidebar())}
            >
              Add wallet
            </button>
          </div>
        </div>

        <EntityDataTable<Wallet>
          paginationKey={PaginationKey.workspaceWallets}
          entity={Entity.wallet}
          items={filteredWallets}
          properties={properties}
        />

        <PaymentTransactionsDataTable
          className="mt-6"
          paginationKey={PaginationKey.workspacePaymentTransactions}
          title="Wallet payment transactions"
          walletAddresses={map(filteredWallets, "address")}
          loading={isLoading}
        />
      </>
    );
  }, [
    currentWorkspaceAllowedForCommunityPlan,
    currentWorkspaceIsLocked,
    filteredWalletLabels,
    filteredWallets,
    isLoading,
    membersLimitExceeded,
    properties,
    removeAllWallets,
    setModalWarningConfig,
    smartLabelFilters,
    toggleAddWalletSidebar,
    wallets,
    wrapActionWithAuth
  ]);

  return (
    <div className="overflow-y-auto">
      <TablePropertiesSidebar<Wallet>
        entity={Entity.wallet}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      {renderPageContent}
    </div>
  );
};

export default WalletsData;
